import React, { useContext, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { FieldArray, Formik, FormikHelpers } from "formik";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { HvGrid } from "@hitachivantara/uikit-react-core";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import usePreventNavigation from "lib/hooks/usePreventNavigation";
import {
  updateCamera,
  CameraAttributes,
  useCamera,
  CameraTypesValues,
  IconData,
  useEntityTypeCamera,
  geocodeLatLng,
  useEntityTypeVms,
  VmsAttributes,
  useCameraPresets,
  getCamera,
  getCameraPresets,
} from "lib/api";
import Container from "components/layout/Container";
import Loading from "components/layout/Loading";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";
import Details from "components/camera/AddEdit/Details";
import Location from "components/camera/AddEdit/Location";
import CameraProperties from "components/camera/AddEdit/Properties";
import CameraType from "components/camera/AddEdit/CameraType";
import Icon from "components/common/Icon";
import OverflowTooltip from "@hitachivantara/uikit-react-core/dist/OverflowTooltip";
import { makeStyles } from "@material-ui/core";
import { useVmsesConfig } from "lib/api/hvsConfigs";
import { useEntity } from "lib/api/entity";
import { UserDataContext } from "lib/context/UserDataContext";
import { UserPolicyTypes } from "lib/api/userSettings";
import Positioning from "components/camera/AddEdit/Positioning";
import { patchPresetsAzimuth } from "lib/api/preset";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";
import {
  cameraFromToCameraEntity,
  useCameraInitialValues,
  useCameraValidation,
} from "../Common";

const Edit: React.FC = () => {
  const { t } = useTranslation("cameraAddEdit");
  const history = useHistory();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { id } = useParams<{ id: string }>();
  const { data: vmsesConfig } = useVmsesConfig({ suspense: true });
  const { data: cameraType } = useEntityTypeCamera({ suspense: true });
  const { data: vmsType } = useEntityTypeVms({ suspense: true });
  const { data: camera, mutate: updateCameraData } = useCamera(
    id,
    cameraType?.data.id ?? "",
    {
      suspense: true,
    }
  );
  const { data: vms } = useEntity(
    (
      camera?.data?.attributes[CameraAttributes.Type]?.value as string
    ).toLowerCase() === CameraTypesValues.VMS
      ? camera?.data.host_id
      : undefined,
    vmsType?.data.id,
    true,
    {
      suspense: true,
    }
  );
  const { data: presets, mutate: updatePresetsData } = useCameraPresets(
    camera?.data.id ?? "",
    !vms,
    {
      suspense: true,
    }
  );
  const { hasSystemTypePolicy } = useContext(UserDataContext);

  const vmsconfig = vmsesConfig?.data.options.find((configs) => {
    return (
      configs.key ===
      (vms?.data?.attributes[VmsAttributes.Vendor]?.value as string)
    );
  });

  const [, setPreventNavigation] = usePreventNavigation();
  const [showBanner, setShowBanner] = useState(false);
  const [saveOpInProgress, setOpInProgress] = useState(false);
  const [showBannerText, setShowBannerText] = useState(t("loading") as string);

  useEffect(() => {
    if (
      (!camera?.data?.address || camera?.data?.address === "") &&
      !Number.isNaN(Number(camera?.data?.location?.coordinates[0])) &&
      !Number.isNaN(Number(camera?.data?.location?.coordinates[1]))
    ) {
      const getAddress = async () => {
        try {
          if (camera && camera.data) {
            const geocode = await geocodeLatLng({
              lat: Number(camera?.data?.location?.coordinates[1]),
              lng: Number(camera?.data?.location?.coordinates[0]),
            });
            await updateCamera(camera.data.id, camera.data.type_id, {
              ...camera.data,
              address: geocode?.data.formattedAddress ?? "",
            });
            camera.data.address = geocode?.data.formattedAddress ?? "";
            updateCameraData(camera);
          }
        } catch (e) {
          // do nothing
        }
      };
      getAddress();
    }
  }, [camera, updateCameraData]);
  const validationSchema = useCameraValidation(t);

  const innerClasses = useMemo(
    () =>
      makeStyles(() => ({
        background: {
          backgroundColor: cameraType?.data.appearance?.color,
          "& svg": {
            color: "white",
          },
        },
      })),
    [cameraType?.data.appearance]
  )();

  const initialValues = useCameraInitialValues(
    camera?.data,
    cameraType?.data,
    vms?.data,
    vmsconfig,
    presets?.data,
    hasSystemTypePolicy
  );

  const sections = useMemo(() => {
    return initialValues.presetPositioningSectionShow
      ? [
          { label: t("details.title"), value: "details" },
          { label: t("cameraType.title"), value: "type" },
          { label: t("location.title"), value: "location" },
          { label: t("positioning.title"), value: "positioning" },
          { label: t("properties.title"), value: "properties" },
        ]
      : [
          { label: t("details.title"), value: "details" },
          { label: t("cameraType.title"), value: "type" },
          { label: t("location.title"), value: "location" },
          { label: t("properties.title"), value: "properties" },
        ];
  }, [initialValues.presetPositioningSectionShow, t]);
  const onSave = async (data: CameraFormData) => {
    // first try to update preset azimuth (if any)
    let presetUpdated = false;
    const changedPresets = data.presetsPositioning?.filter(
      (p) =>
        initialValues.presetsPositioning?.find((pp) => pp.id === p.id)
          ?.azimuth !== p.azimuth
    );
    if (changedPresets && changedPresets.length > 0 && presets?.data) {
      try {
        await patchPresetsAzimuth(
          presets.data.find(() => true)?.type_id ?? "",
          changedPresets
        );
        presetUpdated = true;
        try {
          const newPresetData = (await getCameraPresets(camera?.data.id ?? ""))
            .data;
          updatePresetsData(newPresetData);
          // eslint-disable-next-line no-empty
        } catch (e) {}
      } catch (e) {
        throw new Error(t("validation.unknownError.edit"));
      }
    }
    try {
      const cameraNewData = cameraFromToCameraEntity(
        data,
        camera?.data,
        initialValues
      );
      await updateCamera(id, cameraType?.data.id ?? "", {
        ...camera?.data,
        ...cameraNewData,
      });
      try {
        const newcameraData = (await getCamera(id, cameraType?.data.id ?? ""))
          .data;
        updateCameraData(newcameraData);
        // eslint-disable-next-line no-empty
      } catch (e) {}
    } catch (e) {
      if (presetUpdated) {
        throw new Error(t("validation.unknownError.editButAzimuth"));
      } else {
        throw new Error(t("validation.unknownError.edit"));
      }
    }
  };

  const onSubmitHandler = async (
    values: CameraFormData,
    actions: FormikHelpers<CameraFormData>
  ) => {
    try {
      setOpInProgress(true);
      await onSave(values);
      // mutate(useCamera(id, cameraType?.data.id ?? "").data);
      enqueueSnackbar(t("validation.success.update"), { variant: "success" });
      setPreventNavigation(false);
      actions.setStatus({});
      setOpInProgress(false);
      history.push("/settings/camera");
    } catch (error) {
      setShowBannerText((error as Error)?.message);
      setShowBanner(true);
    }
  };

  return (
    <Formik
      initialStatus={{ serverSideErrors: {} }}
      initialValues={initialValues as CameraFormData}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          if (!initialValues.canEdit) {
            setPreventNavigation(false);
          }
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            <style>
              {
                "\
              .HvTable-tr{ \
                height:  auto !important; \
                min-height:  37px !important; \
                }"
              }
            </style>
            {(isSubmitting || saveOpInProgress) && (
              <Loading
                delay={500}
                loadingLabel={t("submiting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={showBannerText}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={
                  <>
                    <div
                      className={clsx(
                        classes.formHeaderIcon,
                        innerClasses.background
                      )}
                    >
                      <Icon
                        iconData={
                          (cameraType?.data.appearance as IconData) || {}
                        }
                        {...(cameraType?.data.appearance &&
                          cameraType?.data.appearance?.icon_type ===
                            "fontawesome" && {
                            color: "#FFFFFF",
                          })}
                      />
                    </div>
                    <OverflowTooltip
                      data={camera?.data.name as string}
                      className={clsx(classes.title)}
                    />
                  </>
                }
                sections={sections}
                onCancel={goBackHandler}
                saveDisable={!initialValues.canEdit}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.detailsTabMargin}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <Details mode="edit" />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <CameraType type={initialValues.type} mode="edit" />
                </HvGrid>
                <HvGrid
                  className={classes.detailsTabMargin}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <Location />
                </HvGrid>
                {initialValues.presetPositioningSectionShow ? (
                  <HvGrid
                    className={classes.positioningTabMargin}
                    tabIndex={-1}
                    id={sections[3].value}
                    xs={12}
                    item
                  >
                    <Positioning />
                  </HvGrid>
                ) : (
                  <></>
                )}
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={
                    sections[initialValues.presetPositioningSectionShow ? 4 : 3]
                      .value
                  }
                  xs={12}
                  item
                >
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <CameraProperties {...arrayHelpers} />
                    )}
                  />
                </HvGrid>
              </HvGrid>
            </form>
          </Container>
        );
      }}
    </Formik>
  );
};
export const CameraEditAuth: React.FC = () => {
  const { hasSystemTypeNamePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasSystemTypeNamePolicy("camera", UserPolicyTypes.enable);
  }, [hasSystemTypeNamePolicy]);

  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <Edit />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default Edit;
